/* eslint-disable max-lines */
import { AgGridAngular } from '@ag-grid-community/angular';
import {
  ColumnResizedEvent,
  ColumnState,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
  IRowNode,
  RowGroupOpenedEvent,
  RowNode,
} from '@ag-grid-community/core';
import { AsyncPipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import minMax from 'dayjs/plugin/minMax';
import { differenceWith, isBoolean, isEqual } from 'lodash-es';
import { BehaviorSubject, combineLatest, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';

import { ExportExcelButtonComponent } from '@features/export-excel-button/export-excel-button.component';
import { SaveChangesComponent } from '@features/save-changes/save-changes.component';
import { WorkflowPanelComponent } from '@features/workflow-panel/workflow-panel.component';

import { MilestoneQuery } from '@models/milestone-category/milestone/milestone.query';
import { TimelineQuery } from '@models/milestone-category/milestone/timeline.query';
import {
  TimelineGridData,
  TimelineService,
  UpdateDependencyInput,
  UpdateTimelineMilestone,
} from '@models/milestone-category/milestone/timeline.service';
import { MilestoneCategoryQuery } from '@models/milestone-category/milestone-category.query';
import { MilestoneCategoryService } from '@models/milestone-category/milestone-category.service';

import { AgActionsComponent } from '@shared/ag-components/ag-actions/ag-actions.component';
import {
  AgCellWrapperComponent,
  getWrapperCellOptions,
} from '@shared/ag-components/ag-cell-wrapper/ag-cell-wrapper.component';
import { ButtonComponent } from '@shared/components/button/button.component';
import { InputComponent } from '@shared/components/input/input.component';
import {
  ConfirmationActionModalComponent,
  ConfirmationActionModalData,
} from '@shared/components/modals/confirmation-action-modal/confirmation-action-modal.components';
import { ConfirmationModalComponent } from '@shared/components/modals/confirmation-modal/confirmation-modal.component';
import {
  RemoveDialogComponent,
  RemoveDialogInput,
} from '@shared/components/remove-dialog/remove-dialog.component';
import { LocalStorageKey } from '@shared/constants/localStorageKey';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { ROUTING_PATH } from '@shared/constants/routingPath';
import { TableConstants } from '@shared/constants/table.constants';
import { StickyGridDirective } from '@shared/directives/sticky-grid/sticky-grid.directive';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import {
  CreateTimelineMilestoneInput,
  EntityType,
  EventType,
  GqlService,
  listTimelineMilestonesQuery,
  PermissionType,
  WorkflowStep,
} from '@shared/services/gql.service';
import { LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { OverlayService } from '@shared/services/overlay.service';
import { AuthQuery } from '@shared/store/auth/auth.query';
import { AuthService } from '@shared/store/auth/auth.service';
import { EventQuery } from '@shared/store/event/event.query';
import { EventService } from '@shared/store/event/event.service';
import { MainQuery } from '@shared/store/main/main.query';
import { WorkflowQuery } from '@shared/store/workflow/workflow.query';
import { ExportType, Utils } from '@shared/utils/utils';

import { TimelineDialogComponent } from './components/timeline-dialog/timeline-dialog.component';
import { TimelineDialogFormValue } from './components/timeline-dialog/timeline-dialog.model';

dayjs.extend(isBetween);
dayjs.extend(minMax);

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    InputComponent,
    FormsModule,
    ButtonComponent,
    TooltipDirective,
    NgClass,
    AgGridAngular,
    WorkflowPanelComponent,
    SaveChangesComponent,
    ExportExcelButtonComponent,
    StickyGridDirective,
  ],
  selector: 'aux-timeline',
  templateUrl: './timeline.component.html',
})
export class TimelineComponent {
  timelineQuery = inject(TimelineQuery);

  private authQuery = inject(AuthQuery);
  private authService = inject(AuthService);
  private destroyRef = inject(DestroyRef);
  private eventQuery = inject(EventQuery);
  private eventService = inject(EventService);
  private gqlService = inject(GqlService);
  private launchDarklyService = inject(LaunchDarklyService);
  private mainQuery = inject(MainQuery);
  private milestoneCategoryQuery = inject(MilestoneCategoryQuery);
  private milestoneCategoryService = inject(MilestoneCategoryService);
  private milestoneQuery = inject(MilestoneQuery);
  private overlayService = inject(OverlayService);
  private timelineService = inject(TimelineService);
  private workflowQuery = inject(WorkflowQuery);

  workflowName = WorkflowStep.WF_STEP_MONTH_CLOSE_LOCK_TIMELINE;
  isTimeLineFinalized$ = this.workflowQuery.getLockStatusByWorkflowStepType$(this.workflowName);
  userHasModifyPermissions$ = new BehaviorSubject<boolean>(false);

  disabled$ = combineLatest([this.userHasModifyPermissions$, this.isTimeLineFinalized$]).pipe(
    map(([hasPermission, isFinalized]) => {
      const isAdmin = this.authQuery.getValue().is_admin;

      if (isFinalized) {
        this.disabledStateTooltipText$.next(this.messagesConstants.PAGE_LOCKED_FOR_PERIOD_CLOSE);
      } else if (!hasPermission && !isAdmin) {
        this.disabledStateTooltipText$.next(
          this.messagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION
        );
      } else {
        this.disabledStateTooltipText$.next('');
      }

      return isFinalized || (!hasPermission && !isAdmin);
    })
  );

  btnLoading$ = new BehaviorSubject<'export' | false>(false);
  createInputs: Omit<CreateTimelineMilestoneInput, 'timeline_id'>[] = [];
  deleteInputs: string[] = [];
  disabledStateTooltipText$ = new BehaviorSubject('');
  expandedState: Record<string, boolean> = {};
  gridAPI!: GridApi;
  gridData$ = new BehaviorSubject<TimelineGridData[]>([]);
  iCloseMonthsProcessing$ = this.eventQuery.selectProcessingEvent$(EventType.CLOSE_TRIAL_MONTH);
  gridOptions = {
    defaultColDef: {
      ...TableConstants.DEFAULT_GRID_OPTIONS.DEFAULT_COL_DEF,
      cellRenderer: AgCellWrapperComponent,
      minWidth: TableConstants.ACTIONS_WIDTH,
      sortable: false,
    },
    ...TableConstants.DEFAULT_GRID_OPTIONS.GRID_OPTIONS,
    columnDefs: [
      {
        cellClass: TableConstants.STYLE_CLASSES.CELL_JUSTIFY_CENTER,
        cellRendererParams: {
          deleteClickFN: ({ rowNode }: { rowNode: RowNode }) => {
            void this.onRemoveLine(rowNode);
          },
          disabled$: this.disabled$,
          editClickFN: ({ rowNode }: { rowNode: RowNode }) => {
            void this.onUpdateLine(rowNode);
          },
          hideDeleteButton: false,
          processing$: this.iCloseMonthsProcessing$,
          tooltipText$: this.disabledStateTooltipText$,
        },
        cellRendererSelector: (params: ICellRendererParams) => {
          return params.node.group || !params.node.parent ? '' : { component: AgActionsComponent };
        },
        editable: false,
        field: 'actions',
        getQuickFilterText: () => '',
        headerName: '',
        suppressSizeToFit: true,
        width: TableConstants.ACTIONS_WIDTH,
      },
      {
        field: 'milestone_category_name',
        getQuickFilterText: () => '',
        headerName: 'Category',
        hide: true,
        rowGroup: true,
      },
      {
        cellClass: 'text-left',
        cellRenderer: TableConstants.AG_SYSTEM.AG_GROUP_CELL_RENDERER,
        field: 'milestone_name',
        headerName: 'Name',
        minWidth: 125,
        resizable: true,
        showRowGroup: true,
        tooltipField: 'milestone_name',
        width: 250,
        ...getWrapperCellOptions(),
        cellRendererParams: {
          suppressCount: true,
        },
      },
      {
        cellClass: 'text-left',
        field: 'description',
        getQuickFilterText: () => '',
        headerName: 'Description',
        minWidth: 125,
        resizable: true,
        tooltipField: 'description',
        width: 250,
      },
      {
        cellClass: 'text-left',
        field: 'track_from_milestone_name',
        getQuickFilterText: () => '',
        headerName: 'Track From',
        minWidth: 100,
        resizable: true,
        tooltipField: 'track_from_milestone_name',
        width: 200,
      },
      {
        cellClass: TableConstants.STYLE_CLASSES.CELL_ALIGN_RIGHT,
        field: 'contract_month_difference',
        getQuickFilterText: () => '',
        headerName: 'Months',
        minWidth: 100,
        resizable: true,
        width: 150,
      },
      {
        cellClass: TableConstants.STYLE_CLASSES.CELL_ALIGN_RIGHT,
        field: 'startDate',
        getQuickFilterText: () => '',
        headerName: 'Start Date',
        minWidth: 105,
        resizable: true,
        valueFormatter: (val) => {
          if (!val.data) {
            return '';
          }

          const { actual_start_date, contract_start_date, revised_start_date } = val.data;

          if (actual_start_date) return `${actual_start_date}`;

          if (revised_start_date) return `${revised_start_date}`;

          return `${contract_start_date}`;
        },
        width: 150,
      },
      {
        cellClass: TableConstants.STYLE_CLASSES.CELL_ALIGN_RIGHT,
        field: 'endDate',
        getQuickFilterText: () => '',
        headerName: 'End Date',
        minWidth: 105,
        resizable: true,
        valueFormatter: (val) => {
          if (!val.data) {
            return '';
          }

          const { actual_end_date, contract_end_date, revised_end_date } = val.data;

          if (actual_end_date) return `${actual_end_date}`;

          if (revised_end_date) return `${revised_end_date}`;

          return `${contract_end_date}`;
        },
        width: 150,
      },
    ],
    getRowClass: Utils.oddEvenRowClass,
    groupDisplayType: TableConstants.AG_SYSTEM.CUSTOM,
    initialGroupOrderComparator: (params) => {
      const a = this.milestoneCategoryQuery.getEntity(params.nodeA.key)?.grouping_order || 0;
      const b = this.milestoneCategoryQuery.getEntity(params.nodeB.key)?.grouping_order || 0;

      if (a < b) return -1;

      if (a > b) return 1;

      return 0;
    },
    suppressAutoSize: true,
  } as GridOptions;
  hasChanges$ = new BehaviorSubject(false);
  isClosingPanelEnabled$ = this.launchDarklyService.select$(
    (flags) => flags.closing_checklist_toolbar
  );
  isQuarterCloseEnabled$ = this.workflowQuery.isWorkflowAvailable$;
  loadingTimelineQuery = false;
  lSColumnState: ColumnState[] = [];
  readonly messagesConstants = MessagesConstants;
  nameFilterValue = '';
  previousGridData: TimelineGridData[] = [];
  saving = false;
  timelineMonths: string[] = [];
  timelineMonthsRelatedToMilestone: Record<string, TimelineGridData[]> = {};
  updateDependencyInputs: UpdateDependencyInput[] = [];
  updateInputs: UpdateTimelineMilestone[] = [];
  userHasLockTrialTimelinePermission = this.authService.$isAuthorized({
    permissions: [PermissionType.PERMISSION_CHECKLIST_TRIAL_TIMELINE],
    sysAdminsOnly: false,
  });
  valuesBeforeRemove: TimelineGridData[] = [];

  addTimelineMilestone = async () => {
    const ref = await firstValueFrom(
      this.overlayService.openPopup({
        data: {
          forbiddenNames: this.gridData$.value.map((x) => x.milestone_name),
          mode: 'add',
        },
        modal: TimelineDialogComponent,
        settings: {
          header: 'Add Timeline Milestone',
          primaryButton: {
            label: 'Add',
          },
        },
      }).afterClosed$
    );

    if (ref.data) {
      this.createInputs.push(ref.data);
    }
  };

  calculateMonths() {
    const milestones = this.gridData$.getValue();
    const obj: Record<string, TimelineGridData[]> = {};

    // for each month with actuals, get all milestones that are in that month
    this.timelineMonths.forEach((month) => {
      obj[month] = milestones.filter((x) => {
        if (x.contract_start_date && x.contract_end_date) {
          return dayjs(month).isBetween(x.contract_start_date, x.contract_end_date, 'month', '[]');
        }

        return false;
      });
    });

    this.timelineMonthsRelatedToMilestone = obj;
  }

  async canDeactivate(): Promise<boolean> {
    if (this.hasChanges$.getValue()) {
      const result = this.overlayService.openUnsavedChangesConfirmation();
      const event = await firstValueFrom(result.afterClosed$);

      return !!event.data;
    }

    return true;
  }

  checkChanges() {
    this.hasChanges$.next(
      this.createInputs.length > 0 ||
        this.updateInputs.length > 0 ||
        this.updateDependencyInputs.length > 0 ||
        this.deleteInputs.length > 0
    );
  }

  constructor() {
    void this.getStartDate();
    this.initTimelineListValue();

    this.lSColumnState = this.getColumnStatesFromLS();

    this.timelineQuery
      .select()
      .pipe(takeUntilDestroyed())
      .subscribe(({ items }) => {
        this.gridData$.next(this.convertToTimelineGridData(items));
        this.calculateMonths();
      });

    this.timelineQuery
      .selectLoading()
      .pipe(takeUntilDestroyed())
      .subscribe((value) => (this.loadingTimelineQuery = value));

    this.authService
      .isAuthorized$({
        permissions: [PermissionType.PERMISSION_MODIFY_TRIAL_TIMELINE],
        sysAdminsOnly: false,
      })
      .pipe(takeUntilDestroyed())
      .subscribe((x) => {
        this.userHasModifyPermissions$.next(x);
        this.setColumnState();
      });

    this.gridData$.pipe(takeUntilDestroyed()).subscribe((gridData) => {
      if (this.valuesBeforeRemove.length) {
        setTimeout(() => {
          this.gridAPI?.setGridOption('rowData', this.valuesBeforeRemove);
          this.valuesBeforeRemove = [];
        }, 0);
      }

      if (!this.hasChanges$.getValue()) {
        this.gridAPI?.setGridOption('rowData', gridData);
      }

      this.checkChanges();
    });

    this.isTimeLineFinalized$.pipe(takeUntilDestroyed()).subscribe();
  }

  private convertToTimelineGridData(items: listTimelineMilestonesQuery[]): TimelineGridData[] {
    const gridData: TimelineGridData[] = [];

    items.forEach((item) => {
      const track_milestone = this.milestoneQuery.getEntity(item.track_from_milestone?.id);
      const milestone = this.milestoneQuery.getEntity(item.milestone.id);
      const milestone_category_name = this.milestoneCategoryQuery.getEntity(
        item.milestone.milestone_category_id
      );
      const data: TimelineGridData = {
        actual_end_date: item.actual_end_date || null,
        actual_start_date: item.actual_start_date || null,
        contract_end_date: item.contract_end_date,
        contract_month_difference: item.contract_month_difference || null,
        contract_start_date: item.contract_start_date,
        description: milestone?.description || null,
        id: item.id,
        milestone_category_id: item.milestone.milestone_category_id,
        milestone_category_name: milestone_category_name?.name || '',
        milestone_id: item.milestone.id,
        milestone_name: milestone?.name || item.milestone.name || '',
        organizations_with_forecasts: item.milestone.organizations_with_forecasts,
        revised_end_date: item.revised_end_date || null,
        revised_start_date: item.revised_start_date || null,
        track_from_milestone_id: item.track_from_milestone?.id || null,
        track_from_milestone_name: track_milestone?.name || '',
      };

      gridData.push(data);
    });

    return gridData;
  }

  getCanDeleteMilestone({
    contract_end_date,
    contract_start_date,
    milestone_id,
  }: TimelineGridData) {
    const arr = this.timelineMonths
      .map((month) => {
        // do we have any actual month between the contract start and end date
        if (dayjs(month).isBetween(contract_start_date, contract_end_date, 'month', '[]')) {
          // if so, do we have any other milestone in that month
          const milestones = this.timelineMonthsRelatedToMilestone[month].filter(
            (x) => x.milestone_id !== milestone_id
          );

          // if we have any other milestone in that month, we can delete this milestone
          return milestones.length ? '' : dayjs(month).endOf('month').format('YYYY-MM-DD');
        }

        return '';
      })
      .filter(Boolean);

    // if we can't delete the milestone because of a month, return that month
    return arr.length ? arr[0] : '';
  }

  getColumnStatesFromLS() {
    const stringForColumnStates = localStorage.getItem(LocalStorageKey.COLUMN_STATES);
    const presetColumnStates = stringForColumnStates
      ? (JSON.parse(stringForColumnStates) as ColumnState[])
      : [];

    return presetColumnStates;
  }

  getDateConstraints(data: TimelineGridData): {
    endDate: null | string;
    startDate: null | string;
  } {
    const { contract_end_date, contract_start_date, milestone_id } = data;

    let startDate: null | string = null;
    let endDate: null | string = null;

    this.timelineMonths.forEach((month) => {
      // if the month is between the contract start and end date
      if (dayjs(month).isBetween(contract_start_date, contract_end_date, 'month', '[]')) {
        // get all milestones in that month that are not the current milestone
        const milestones = this.timelineMonthsRelatedToMilestone[month].filter(
          (x) => x.milestone_id !== milestone_id
        );

        // if there are no other milestones in that month set the start and end date
        if (!milestones.length) {
          startDate = startDate || dayjs(month).endOf('month').format('YYYY-MM-DD');
          endDate = month;
        }
      }
    });

    const milestones = this.gridData$.getValue();

    if (startDate) {
      for (const x of milestones) {
        if (x.milestone_id === milestone_id) {
          continue;
        }

        // if the milestone is before the start date, set the start date as null
        // this way we don't need to validate the start date
        // because if we change the start date, there is still another milestone that covers the timeline
        if (dayjs(x.contract_start_date).isBefore(startDate)) {
          startDate = null;
          break;
        }
      }
    }

    if (endDate) {
      for (const x of milestones) {
        if (x.milestone_id === milestone_id) {
          continue;
        }

        // if the milestone is after the end date, set the end date as null
        // this way we don't need to validate the end date
        // because if we change the end date, there is still another milestone that covers the timeline
        if (dayjs(x.contract_end_date).isAfter(endDate)) {
          endDate = null;
          break;
        }
      }
    }

    if (endDate == null) {
      const milestones = this.gridData$.getValue();
      const maxEndDate = dayjs.max(
        milestones
          .filter((x) => x.milestone_id !== milestone_id)
          .map((x) => dayjs(x.contract_end_date).startOf('month'))
      );

      if (maxEndDate) {
        for (const month of [...this.timelineMonths].reverse()) {
          // eslint-disable-next-line max-depth
          if (this.timelineMonthsRelatedToMilestone[month].length === 0) {
            const monthDate = dayjs(month);

            // eslint-disable-next-line max-depth
            if (monthDate.isAfter(maxEndDate)) {
              endDate = month;
              break;
            }
          }
        }
      }
    }

    return { endDate, startDate };
  }

  async getStartDate() {
    const { data } = await firstValueFrom(
      this.gqlService.listTrialExpenseTimeline$({
        by_vendor: false,
        expense_type_id: 'EXPENSE_WP',
        nz_expenses_only: true,
      })
    );

    if (data?.length) {
      this.timelineMonths = data.map((x) => x.timeline_month);
      this.calculateMonths();
    }
  }

  private initTimelineListValue(): void {
    combineLatest([this.timelineService.getTimelineItems(), this.milestoneCategoryService.get()])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([timelineResponse]) => {
        this.gridData$.next(this.convertToTimelineGridData(timelineResponse?.data || []));
        this.calculateMonths();
      });
  }

  isBtnLoading(str: string) {
    return this.btnLoading$.pipe(map((x) => x === str));
  }

  onColumnResized(event: ColumnResizedEvent) {
    if (event.source === 'uiColumnResized' && event.finished) {
      this.saveColumnStates();
    }
  }

  onDataRendered({ api }: { api: GridApi }) {
    api.forEachNode((node) => {
      if (node.key && !isBoolean(this.expandedState[node.key])) {
        node.setExpanded(true);
      }
    });
    this.setColumnState();
  }

  async onExportTimeline() {
    const trialName = this.mainQuery.getSelectedTrial()?.short_name || '';
    const dateStr = dayjs(new Date()).format('YYYY.MM.DD-HHmmss');

    if (this.btnLoading$.getValue()) {
      return;
    }

    this.btnLoading$.next('export');

    const { errors, success } = await firstValueFrom(
      this.eventService.processEvent$({
        entity_id: this.mainQuery.getSelectedTrial()?.id || '',
        entity_type: EntityType.TRIAL,
        payload: JSON.stringify({
          export_type: ExportType.TIMELINE,
          filename: `${trialName}_auxilius-trial-timeline__${dateStr}`,
        }),
        type: EventType.GENERATE_EXPORT,
      })
    );

    if (success) {
      this.overlayService.success(
        'Export is being generated and will download when complete. You may leave the page.'
      );
    } else {
      this.overlayService.error(errors);
    }

    this.btnLoading$.next(false);
  }

  onGridReady({ api }: GridReadyEvent) {
    this.gridAPI = api;
    this.setColumnState();
  }

  onGroupOpened({ expanded, node }: RowGroupOpenedEvent) {
    if (node.key) {
      this.expandedState[node.key] = expanded;
    }
  }

  // eslint-disable-next-line max-lines-per-function
  async onRemoveLine(rowNode: RowNode) {
    if (!rowNode.data) {
      return;
    }

    const { id, milestone_id, milestone_name, organizations_with_forecasts } =
      rowNode.data as TimelineGridData;

    const canDeleteMilestone = this.getDateConstraints(rowNode.data);

    const { items } = this.timelineQuery.getValue();
    const tracking_milestones = items.filter((x) => x.track_from_milestone?.id === milestone_id);

    if (
      organizations_with_forecasts.length > 0 ||
      canDeleteMilestone.startDate ||
      canDeleteMilestone.endDate
    ) {
      const cannotDeleteMessage = `${milestone_name} cannot be deleted because it is used in Forecast Methodology.<br>Please first remove the dependencies below and then ${milestone_name} can be successfully deleted.<br>`;
      const routeInputs = [
        {
          componentName: 'Forecast Methodology:',
          link: `/${ROUTING_PATH.FORECAST_ROUTING.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.FORECAST_METHODOLOGY}`,
          name: organizations_with_forecasts.map(({ name }) => name),
        },
      ];

      const removeDialogInput: RemoveDialogInput = {
        canDeleteMilestone,
        cannotDeleteMessage,
        routeInputs,
      };

      const resp = this.overlayService.openPopup({
        content: RemoveDialogComponent,
        data: removeDialogInput,
        settings: {
          header: 'Remove Milestone',
          primaryButton: {
            disabled: true,
            label: 'Remove',
          },
        },
      });

      await firstValueFrom(resp.afterClosed$);
    } else if (tracking_milestones?.length > 0) {
      let milestoneNames = '';

      for (const tracking_milestone of tracking_milestones) {
        milestoneNames += `- ${tracking_milestone.milestone.name}<br>`;
      }

      const resp = await firstValueFrom(
        this.overlayService.openPopup<
          ConfirmationActionModalData,
          boolean,
          ConfirmationActionModalComponent
        >({
          data: {
            hideConfirmationHint: true,
            message: `Are you sure that you want to remove ${milestone_name}? The following milestones will no longer track this milestone:<br>${milestoneNames}`,
            skipKeywordConfirmation: true,
          },
          modal: ConfirmationActionModalComponent,
          settings: {
            header: 'Remove Milestone',
            primaryButton: {
              label: 'Remove',
            },
          },
        }).afterClosed$
      );

      if (resp.data) {
        for (const tracking_milestone of tracking_milestones) {
          const milestone = this.milestoneQuery.getEntity(tracking_milestone.milestone.id);
          const input: UpdateTimelineMilestone = {
            actual_end_date: tracking_milestone.actual_end_date,
            actual_start_date: tracking_milestone.actual_start_date,
            contract_end_date: tracking_milestone.contract_end_date,
            contract_start_date: tracking_milestone.contract_start_date,
            description: milestone?.description || null,
            id: tracking_milestone.id,
            milestone_category_id: tracking_milestone.milestone.milestone_category_id,
            milestone_id: tracking_milestone.milestone.id,
            name: tracking_milestone.milestone.name,
            revised_end_date: tracking_milestone.revised_end_date,
            revised_start_date: tracking_milestone.revised_start_date,
            track_from_milestone_id: null,
          };

          this.timelineService.updateTimelineMilestone(input);
          this.updateInputs.push(input);
        }

        this.gridAPI.applyTransaction({ remove: [rowNode] });
        this.timelineService.deleteTimelineMilestone(id);
        this.deleteInputs.push(id);
        this.hasChanges$.next(true);
      }
    } else {
      this.overlayService.openPopup<
        { message: string },
        boolean | undefined,
        ConfirmationModalComponent
      >({
        content: ConfirmationModalComponent,
        data: {
          message: `Are you sure that you want to remove ${milestone_name}?`,
        },
        settings: {
          header: 'Remove Milestone',
          primaryButton: {
            action: (instance) => {
              instance?.ref.close();
              this.gridAPI.applyTransaction({ remove: [rowNode] });
              this.timelineService.deleteTimelineMilestone(id);
              this.deleteInputs.push(id);
              this.hasChanges$.next(true);
            },
            label: 'Remove',
          },
        },
      });
    }
  }

  onRowDataChanged() {
    const gridData = this.gridData$.value;
    const changedMilestoneCategoryId = differenceWith(gridData, this.previousGridData, isEqual)[0]
      ?.milestone_category_id;

    this.gridAPI?.forEachNode((rowNode: IRowNode) => {
      if (rowNode.key) {
        const currentExpandedState = isBoolean(this.expandedState[rowNode.key])
          ? this.expandedState[rowNode.key]
          : true;
        const expanded = rowNode.key === changedMilestoneCategoryId ? true : currentExpandedState;

        rowNode.setExpanded(expanded);
      }
    });

    this.setColumnState();
    this.checkChanges();
    this.previousGridData = gridData;
  }

  onSaveAll = async () => {
    this.saving = true;

    if (this.deleteInputs.length > 0) {
      this.deleteInputs.forEach((x) => {
        this.timelineService.deleteTimelineMilestone(x);
      });
    }

    const resp = await this.timelineService.saveChanges(
      this.createInputs,
      this.updateInputs,
      this.updateDependencyInputs,
      this.deleteInputs,
      this.gridData$.getValue()
    );

    if (resp.success) {
      this.overlayService.success(`Successfully Saved`);
      this.timelineService.getTimelineItems().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    } else if (resp.errors.length > 0) {
      this.overlayService.error(resp.errors);
      // allow time for the user to see the error message before refreshing
      await new Promise((resolve) => setTimeout(resolve, 2000));

      location.reload();
    } else {
      this.overlayService.error(`An error has occurred`);
    }

    this.createInputs = [];
    this.updateInputs = [];
    this.updateDependencyInputs = [];
    this.deleteInputs = [];
    this.saving = false;
    this.hasChanges$.next(false);
  };

  onUpdateLine(rowNode: RowNode) {
    if (!rowNode.data) {
      return;
    }

    const {
      actual_end_date,
      actual_start_date,
      contract_end_date,
      contract_start_date,
      description,
      id,
      milestone_category_id,
      milestone_id,
      milestone_name,
      revised_end_date,
      revised_start_date,
      track_from_milestone_id,
    } = rowNode.data as TimelineGridData;

    const { endDate, startDate } = this.getDateConstraints(rowNode.data as TimelineGridData);

    const ref = this.overlayService.openPopup<
      {
        endDate: null | string;
        forbiddenNames: string[];
        formValue?: TimelineDialogFormValue;
        milestone_id?: string;
        mode: 'add' | 'edit';
        startDate: null | string;
        timeline_milestone_id?: string;
      },
      { update: UpdateTimelineMilestone; updateDependency: UpdateDependencyInput[] }
    >({
      data: {
        endDate,
        forbiddenNames: this.gridData$.value
          .filter((x) => x.milestone_name !== milestone_name)
          .map((x) => x.milestone_name),
        formValue: {
          actual_end_date,
          actual_start_date,
          contract_end_date,
          contract_start_date,
          description,
          id,
          milestone_category_id,
          milestone_id,
          name: milestone_name,
          revised_end_date,
          revised_start_date,
          track_from_milestone_id,
        },
        milestone_id,
        mode: 'edit',
        startDate,
        timeline_milestone_id: id,
      },
      modal: TimelineDialogComponent,
      settings: {
        header: 'Edit Timeline Milestone',
        primaryButton: {
          label: 'Update',
        },
      },
    });

    ref.afterClosed$.subscribe((resp) => {
      if (resp.data) {
        this.updateInputs.push(resp.data.update);
        this.updateDependencyInputs.push(...resp.data.updateDependency);
      }
    });
  }

  reset(): void {
    this.createInputs = [];
    this.updateInputs = [];
    this.updateDependencyInputs = [];
    this.deleteInputs = [];
    this.hasChanges$.next(false);
    this.initTimelineListValue();
  }

  saveColumnStates() {
    const api = this.gridAPI;
    const columnStates = api.getColumnState();

    localStorage.setItem(LocalStorageKey.COLUMN_STATES, JSON.stringify(columnStates));
  }

  setColumnState() {
    const api = this.gridAPI;

    if (api) {
      const presetColumnStates = this.lSColumnState;

      if (presetColumnStates.length) {
        // normally it should work with just applying the previous column states, but for some
        // reason the grid does not apply the widths of the columns so we have to apply them manually.
        // Just storing and applying widths also does not work, since grid adjusted itself to fit columns to its width,
        // therefore any adjustment applied to widths ruins the grid layout
        api.applyColumnState({ applyOrder: true, state: presetColumnStates });
        presetColumnStates.forEach((colState) => {
          if (colState.width && !colState.hide) {
            api.getColumn(colState.colId)?.setActualWidth(colState.width);
          }
        });
      } else {
        api.sizeColumnsToFit();
      }
    }
  }

  sizeColumnsToFit(): void {
    this.gridAPI?.sizeColumnsToFit();
  }
}
